import React, { useEffect, useRef, useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  CircularProgress,
  Tooltip,
  Container
} from '@mui/material';
import { themeColors } from '../styles/theme';
import Fonts from './Fonts';
import { useNavigate } from 'react-router-dom';
import TopNav from './TopNav';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import SearchIcon from '@mui/icons-material/Search';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Footer } from './Footer';
import { 
  trackAreaSearch, 
  trackNearMeSearch, 
  trackMapPinPlaced, 
  trackSearchAreaSelectionMethod
} from '../utils/analytics';
import { useLocation } from '../contexts/LocationContext';

// Import leaflet and CSS
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Set up Leaflet icon paths
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

interface SearchAreaProps {
  setTitle: React.Dispatch<React.SetStateAction<string>>;
  setShowBurgerMenuIcon: React.Dispatch<React.SetStateAction<boolean>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const DEFAULT_ZOOM = 13;

// Fixed radius - always 1km
const FIXED_RADIUS = 1000; // 1000m = 1km

const SearchArea: React.FC<SearchAreaProps> = ({ 
  setTitle, 
  setShowBurgerMenuIcon, 
  setLoading 
}) => {
  const { 
    userLocation,
    searchLocation,
    setCustomSearchLocation,
    requestUserLocation,
    isLoadingUserLocation
  } = useLocation();
  
  const navigate = useNavigate();
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const circleRef = useRef<L.Circle | null>(null);
  
  const [mapInitialized, setMapInitialized] = useState(false);
  const [locationMarked, setLocationMarked] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get initial coordinates from context
  const getInitialCoordinates = () => {
    if (searchLocation) {
      return searchLocation;
    }
    if (userLocation) {
      return userLocation;
    }
    // Fallback to London
    return { latitude: 51.5074, longitude: -0.1278 };
  };
  
  const initialCoords = getInitialCoordinates();
  const [customLatitude, setCustomLatitude] = useState(initialCoords.latitude);
  const [customLongitude, setCustomLongitude] = useState(initialCoords.longitude);

  // Set up page title and navigation elements
  useEffect(() => {
    setTitle('Search Area');
    setShowBurgerMenuIcon(true);
  }, [setTitle, setShowBurgerMenuIcon]);

  // Update center circle based on map center
  const updateCenterCircle = () => {
    if (!mapRef.current) return;
    
    const center = mapRef.current.getCenter();
    const lat = center.lat;
    const lng = center.lng;
    
    // Remove existing circle
    if (circleRef.current) {
      circleRef.current.remove();
    }
    
    // Add circle at map center
    const circle = L.circle([lat, lng], {
      radius: FIXED_RADIUS,
      fillColor: themeColors.orange,
      fillOpacity: 0.2,
      color: themeColors.orange,
      weight: 2
    }).addTo(mapRef.current);
    circleRef.current = circle;
    
    // Update coordinates
    setCustomLatitude(lat);
    setCustomLongitude(lng);
  };

  // Handle map movement to update center circle
  const handleMapMove = () => {
    updateCenterCircle();
  };

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current) return;
    
    const startLat = initialCoords.latitude;
    const startLng = initialCoords.longitude;
    
    setCustomLatitude(startLat);
    setCustomLongitude(startLng);
    
    // Clean up previous map instance
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
      markerRef.current = null;
      circleRef.current = null;
    }
    
    try {
      // Create map
      const map = L.map(mapContainerRef.current, {
        attributionControl: false,
        zoomControl: true,
        scrollWheelZoom: true
      }).setView([startLat, startLng], DEFAULT_ZOOM);
      
      mapRef.current = map;
      
      // Add tile layer
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      }).addTo(map);
      
      // Add center circle that follows map center
      updateCenterCircle();
      setLocationMarked(true);
      
      // Track that we're using stored location
      if (searchLocation) {
        trackSearchAreaSelectionMethod('stored_search_location', startLat, startLng);
      } else if (userLocation) {
        trackSearchAreaSelectionMethod('stored_location', startLat, startLng);
      }
      
      // Add moveend handler to update coordinates when map is panned
      map.on('moveend', handleMapMove);
      
      // Invalidate size to ensure proper rendering
      map.invalidateSize();
      
      // Set map as initialized
      setMapInitialized(true);
      
    } catch (error) {
      console.error('Error initializing map:', error);
      setError('Failed to initialize map');
    }
    
    // Clean up on unmount
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markerRef.current = null;
        circleRef.current = null;
      }
    };
  }, [initialCoords.latitude, initialCoords.longitude, searchLocation, userLocation]);

  // Handle use current location button
  const handleUseCurrentLocation = async () => {
    setError(null);
    
    const location = await requestUserLocation(true); // Force refresh
    
    if (location && mapRef.current) {
      // Track location usage for search area
      trackNearMeSearch(location.latitude, location.longitude);
      
      // Track selection method
      trackSearchAreaSelectionMethod('current_location', location.latitude, location.longitude);
      
      // Update map view and center circle will update automatically
      mapRef.current.setView([location.latitude, location.longitude], DEFAULT_ZOOM);
      setLocationMarked(true);
    } else {
      setError('Failed to get your location. Please allow location access or select a location manually.');
    }
  };

  // Handle search button click
  const handleSearch = () => {
    if (!locationMarked) {
      setError('Please select a location on the map first');
      return;
    }
    
    // Set custom search location in context
    setCustomSearchLocation(customLatitude, customLongitude);
    
    // Track the search area event with location and radius
    trackAreaSearch(customLatitude, customLongitude, FIXED_RADIUS);
    
    // Navigate to the pubs page
    setLoading(true);
    navigate('/pubs');
  };

  // Handle back button click
  const handleBack = () => {
    navigate('/');
  };

  return (
    <Box sx={{ width: '100%', minHeight: '100vh' }}>
      <TopNav title="Search Area" />
      
      <Container 
        maxWidth="lg" 
        sx={{ 
          pt: '20px',
          pb: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Fonts />
        
        <Box sx={{ 
          width: '100%',
          maxWidth: 800,
          mx: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}>
          
          {error && (
            <Typography 
              variant="body2" 
              sx={{ color: 'error.main', textAlign: 'center', mb: 1 }}
            >
              {error}
            </Typography>
          )}
          
          <Paper 
            elevation={3} 
            sx={{ 
              width: '100%',
              height: '45vh',
              minHeight: '300px',
              borderRadius: '16px',
              overflow: 'hidden',
              position: 'relative',
              mb: 1
            }}
          >
            <div 
              ref={mapContainerRef} 
              style={{ 
                width: '100%', 
                height: '100%',
                backgroundColor: '#f0f0f0',
                position: 'relative',
                zIndex: 1,
                display: 'block'
              }}
            />
            
            {/* Center pin overlay */}
            <Box sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -100%)',
              zIndex: 5,
              pointerEvents: 'none',
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
            }}>
              {/* Pin icon using CSS */}
              <Box sx={{
                width: '24px',
                height: '24px',
                backgroundColor: themeColors.orange,
                borderRadius: '50% 50% 50% 0',
                transform: 'rotate(-45deg)',
                border: '2px solid white',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '8px',
                  height: '8px',
                  backgroundColor: 'white',
                  borderRadius: '50%'
                }
              }} />
            </Box>
            
            {!mapInitialized && (
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                backgroundColor: 'rgba(255,255,255,0.7)'
              }}>
                <CircularProgress size={60} sx={{ mb: 2 }} />
                <Typography variant="h6">Initializing Map...</Typography>
              </Box>
            )}
          </Paper>
          
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 1,
            p: 1.5,
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(16px)',
            borderRadius: { xs: '12px', sm: '16px' },
            boxShadow: '0 3px 10px rgba(0,0,0,0.08)',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
              boxShadow: '0 6px 15px rgba(0,0,0,0.15)',
            }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontWeight: 'medium' }}>
                Search Radius:
              </Typography>
              <Typography color="text.secondary" variant="body2">
                {FIXED_RADIUS}m
              </Typography>
              <Tooltip title="Search radius is fixed to 1km">
                <HelpOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              </Tooltip>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
              <Button
                variant="outlined"
                startIcon={<MyLocationIcon />}
                onClick={handleUseCurrentLocation}
                disabled={isLoadingUserLocation}
                sx={{
                  borderColor: themeColors.blue,
                  color: themeColors.blue,
                  flex: 1,
                  '&:hover': {
                    borderColor: themeColors.blue,
                    backgroundColor: `${themeColors.blue}10`,
                  },
                  py: 0.75,
                  fontSize: '0.875rem'
                }}
              >
                {isLoadingUserLocation ? 'Getting Location...' : 'Use My Location'}
              </Button>
              
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                disabled={!locationMarked}
                sx={{
                  backgroundColor: themeColors.orange,
                  color: '#fff',
                  flex: 1,
                  '&:hover': {
                    backgroundColor: themeColors.mainYellow,
                  },
                  py: 0.75,
                  fontSize: '0.875rem'
                }}
              >
                Search This Area
              </Button>
            </Box>
          </Box>
        </Box>
      </Container>
      
      {/* Add Footer */}
      <Box sx={{ px: '5%' }}>
        <Footer />
      </Box>
    </Box>
  );
};

export default SearchArea;